// POS tables Plano/Lista view preference. Ported from the old POS
// RestaurantPosTables.vue view-pref logic; shares the same localStorage key so
// the user's choice survives the V1↔V2 switch.
import { computed } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { useRestaurantStore } from '@/stores/restaurant.store';

export type TPosTablesViewPref = 'auto' | 'plan' | 'list';

const viewPref = useLocalStorage<TPosTablesViewPref>(
  'adminPosTablesView',
  'auto'
);

export function usePosTablesView() {
  const restaurantStore = useRestaurantStore();

  const sectors = computed(() => restaurantStore.sectors ?? []);

  /** Plan available iff at least one table is placed in a live sector. */
  const planAvailable = computed(() => {
    if (!sectors.value.length || !restaurantStore.tables) return false;
    const liveSectorIds = new Set(sectors.value.map((s) => s.id));
    return restaurantStore.tables.some(
      (t) =>
        t.sectorId !== null &&
        t.posX !== null &&
        t.posY !== null &&
        liveSectorIds.has(t.sectorId)
    );
  });

  /**
   * `auto` resolves to plan-when-available (list otherwise); an explicit
   * "plan" pick is honored even without a plan so the pane can show the
   * "create your first floor plan" call to action.
   */
  const effectiveView = computed<'plan' | 'list'>(() => {
    if (viewPref.value === 'plan') return 'plan';
    if (viewPref.value === 'list') return 'list';
    return planAvailable.value ? 'plan' : 'list';
  });

  function setView(view: 'plan' | 'list') {
    viewPref.value = view;
  }

  return {
    sectors,
    planAvailable,
    effectiveView,
    setView,
  };
}
